import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  Repeat, Pause, Play, Trash, Plus, Lightning, EnvelopeSimple, CheckCircle,
  CalendarBlank, Receipt,
} from "@phosphor-icons/react";

const FREQ_OPTIONS = [
  { value: 1, label: "Mensual" },
  { value: 2, label: "Cada 2 meses" },
  { value: 3, label: "Trimestral" },
  { value: 4, label: "Cada 4 meses" },
  { value: 6, label: "Semestral" },
  { value: 12, label: "Anual" },
];

function fmtEur(n) {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
}

export default function RecurringInvoices() {
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // contrato o null
  const [openForm, setOpenForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, cusRes] = await Promise.all([
        api.get("/recurring/contracts"),
        api.get("/customers"),
      ]);
      setContracts(cRes.data.items || []);
      setCustomers(cusRes.data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setOpenForm(true); };
  const openEdit = (c) => { setEditing(c); setOpenForm(true); };

  const togglePause = async (c) => {
    try {
      const url = c.active ? `/recurring/contracts/${c.id}/pause` : `/recurring/contracts/${c.id}/resume`;
      await api.post(url);
      toast.success(c.active ? "Contrato pausado" : "Contrato reanudado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const runNow = async (c) => {
    if (!window.confirm(`Generar factura YA para "${c.description}"?\n\nImporte: ${fmtEur(c.amount * 1.21)} (con IVA)\nCliente: ${c.customer_name}\n${c.send_email ? "→ Se enviará por email" : "→ NO se enviará por email"}`)) return;
    try {
      const { data } = await api.post(`/recurring/contracts/${c.id}/run-now`);
      toast.success(`✓ Factura ${data.invoice_number} generada · ${data.email_sent ? "Email enviado" : "Sin email"}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Borrar el contrato "${c.description}"?\n\nLas facturas ya emitidas se mantienen, pero no se generarán más.`)) return;
    try {
      await api.delete(`/recurring/contracts/${c.id}`);
      toast.success("Contrato borrado");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="recurring-invoices-page">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">Facturación</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">Contratos recurrentes</h1>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2 max-w-2xl">
            Programa facturas que se emiten y envían automáticamente por email a tus clientes B2B/flotas cada mes, trimestre o año.
          </p>
        </div>
        <Button onClick={openCreate} className="ag-btn-gold h-10" data-testid="recurring-create-btn">
          <Plus size={16} className="mr-1.5" /> Nuevo contrato
        </Button>
      </header>

      {loading ? (
        <div className="text-sm text-[var(--ag-text-dim)]">Cargando…</div>
      ) : contracts.length === 0 ? (
        <div className="ag-card p-12 text-center">
          <Repeat size={36} weight="duotone" className="text-[var(--ag-gold)] mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold tracking-tighter">Aún no tienes contratos recurrentes</h2>
          <p className="text-sm text-[var(--ag-text-dim)] mt-2 max-w-md mx-auto">
            Crea tu primer contrato y delega la facturación periódica de tus clientes recurrentes. Sólo se factura, no se cobra automáticamente (eso lo gestionas tú).
          </p>
          <Button onClick={openCreate} className="ag-btn-gold mt-5">
            <Plus size={14} className="mr-1.5" /> Crear contrato
          </Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="recurring-list">
          {contracts.map((c) => (
            <ContractRow key={c.id} c={c} onEdit={openEdit} onPause={togglePause} onRun={runNow} onDel={del} />
          ))}
        </div>
      )}

      <ContractFormDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        contract={editing}
        customers={customers}
        onSaved={() => { setOpenForm(false); load(); }}
      />
    </div>
  );
}

function ContractRow({ c, onEdit, onPause, onRun, onDel }) {
  const totalConIva = c.amount * 1.21;
  return (
    <div className={`ag-card p-5 ${c.active ? "" : "opacity-60"}`} data-testid={`recurring-row-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-base tracking-tight">{c.description}</h3>
            {c.active
              ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 rounded">Activo</span>
              : <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded">Pausado</span>}
            {c.send_email
              ? <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)] rounded flex items-center gap-1"><EnvelopeSimple size={9} weight="bold" /> Email auto</span>
              : null}
          </div>
          <div className="text-xs text-[var(--ag-text-dim)] mt-1">{c.customer_name}</div>
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-2 flex flex-wrap gap-x-3 gap-y-1">
            <span><CalendarBlank size={10} className="inline mr-1" /> Día {c.day_of_month} de cada {FREQ_OPTIONS.find(f=>f.value===c.frequency_months)?.label.toLowerCase() || `${c.frequency_months} meses`}</span>
            <span>· {fmtEur(c.amount)} + IVA = <strong className="text-[var(--ag-text)] tabular-nums">{fmtEur(totalConIva)}</strong></span>
            {c.next_run_date && <span>· Próx. emisión: <strong className="text-[var(--ag-text)]">{c.next_run_date}</strong></span>}
            {c.last_run_date && <span>· Última: {c.last_run_date}</span>}
            <span>· {(c.generated_invoice_ids || []).length} facturas emitidas</span>
          </div>
        </div>
        <div className="flex gap-1 flex-wrap items-center">
          <Button size="sm" variant="outline" onClick={() => onRun(c)} className="h-8 text-[10px] border-[var(--ag-gold)]/40 text-[var(--ag-gold)] hover:bg-[var(--ag-gold)]/10" data-testid={`recurring-run-${c.id}`}>
            <Lightning size={11} className="mr-1" weight="fill" /> Generar ya
          </Button>
          <Button size="sm" variant="outline" onClick={() => onPause(c)} className="h-8 text-[10px] border-[var(--ag-border)]" data-testid={`recurring-toggle-${c.id}`}>
            {c.active ? <><Pause size={11} className="mr-1" /> Pausar</> : <><Play size={11} className="mr-1" /> Reanudar</>}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEdit(c)} className="h-8 text-[10px] border-[var(--ag-border)]">Editar</Button>
          <Button size="sm" variant="ghost" onClick={() => onDel(c)} className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10" data-testid={`recurring-del-${c.id}`}>
            <Trash size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ContractFormDialog({ open, onClose, contract, customers, onSaved }) {
  const isEditing = !!contract;
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    customer_id: "", description: "", amount: 100, frequency_months: 1,
    day_of_month: 1, start_date: today, end_date: "", send_email: true, email_to: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (contract) {
      setForm({
        customer_id: contract.customer_id,
        description: contract.description || "",
        amount: contract.amount,
        frequency_months: contract.frequency_months,
        day_of_month: contract.day_of_month,
        start_date: contract.start_date,
        end_date: contract.end_date || "",
        send_email: !!contract.send_email,
        email_to: contract.email_to || "",
        notes: contract.notes || "",
      });
    } else {
      setForm({
        customer_id: "", description: "", amount: 100, frequency_months: 1,
        day_of_month: 1, start_date: today, end_date: "", send_email: true, email_to: "", notes: "",
      });
    }
  }, [contract, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!form.customer_id) { toast.error("Selecciona un cliente"); return; }
    if (!form.description?.trim()) { toast.error("Descripción obligatoria"); return; }
    if (!form.amount || form.amount <= 0) { toast.error("Importe > 0"); return; }
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        description: form.description.trim(),
        amount: Number(form.amount),
        frequency_months: Number(form.frequency_months),
        day_of_month: Number(form.day_of_month),
        start_date: form.start_date,
        end_date: form.end_date || null,
        send_email: !!form.send_email,
        email_to: form.email_to || undefined,
        notes: form.notes || "",
      };
      if (isEditing) {
        // Update solo permite ciertos campos — borramos los que no se pueden modificar
        const { customer_id: _cid, start_date: _sd, ...updatePayload } = payload;
        await api.put(`/recurring/contracts/${contract.id}`, updatePayload);
        toast.success("Contrato actualizado");
      } else {
        await api.post("/recurring/contracts", payload);
        toast.success("Contrato creado · Las facturas se generarán automáticamente");
      }
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--ag-midnight-soft)] border-[var(--ag-border)] text-[var(--ag-text)] max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl tracking-tighter">
            {isEditing ? "Editar contrato recurrente" : "Nuevo contrato recurrente"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Cliente</Label>
            <select
              value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              disabled={isEditing}
              className="mt-1 w-full h-10 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-sm px-3 text-sm text-[var(--ag-text)]"
              data-testid="recurring-customer-select"
            >
              <option value="">— Selecciona cliente del CRM —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.razon_social ? `· ${c.razon_social}` : ""}</option>
              ))}
            </select>
            {isEditing && <p className="text-[10px] text-[var(--ag-text-dim)] mt-1">El cliente no se puede modificar tras crear el contrato.</p>}
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Concepto / descripción factura</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder='ej: "Detailing mensual flota Pepe Motors"' className="mt-1 h-10" data-testid="recurring-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Importe (sin IVA)</Label>
              <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 h-10 tabular-nums" data-testid="recurring-amount" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Total c/IVA estimado</Label>
              <div className="mt-1 h-10 px-3 flex items-center bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-sm text-sm tabular-nums text-[var(--ag-gold)] font-bold">
                {fmtEur((Number(form.amount) || 0) * 1.21)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Frecuencia</Label>
              <select value={form.frequency_months} onChange={(e) => setForm({ ...form, frequency_months: Number(e.target.value) })}
                className="mt-1 w-full h-10 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-sm px-3 text-sm text-[var(--ag-text)]" data-testid="recurring-frequency">
                {FREQ_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Día de emisión (1-28)</Label>
              <Input type="number" min="1" max="28" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: Number(e.target.value) })} className="mt-1 h-10 tabular-nums" data-testid="recurring-day" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Inicio</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} disabled={isEditing} className="mt-1 h-10" data-testid="recurring-start" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Fin (opcional)</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 h-10" data-testid="recurring-end" />
            </div>
          </div>
          <div className="flex items-center justify-between p-3 rounded-sm border border-[var(--ag-border)] bg-[var(--ag-midnight)]">
            <div>
              <div className="text-sm font-medium flex items-center gap-2"><EnvelopeSimple size={14} weight="duotone" className="text-[var(--ag-gold)]" /> Enviar factura por email al generarse</div>
              <div className="text-[10px] text-[var(--ag-text-dim)] mt-0.5">PDF adjunto vía Resend al email del cliente.</div>
            </div>
            <Switch checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: v })} data-testid="recurring-send-email" />
          </div>
          {form.send_email && (
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Email destinatario (opcional — si vacío usa el del cliente)</Label>
              <Input type="email" value={form.email_to} onChange={(e) => setForm({ ...form, email_to: e.target.value })} placeholder="cliente@empresa.com" className="mt-1 h-10" data-testid="recurring-email-to" />
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">Notas internas (opcional)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 min-h-[60px]" />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="ag-btn-gold" data-testid="recurring-submit">
            {busy ? "Guardando…" : (isEditing ? "Actualizar contrato" : "Crear contrato")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
